import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import api from "../services/api";
import CustomerImportUploadCard from "../components/customerImport/CustomerImportUploadCard";
import CustomerImportMappingStep from "../components/customerImport/CustomerImportMappingStep";
import CustomerImportValidationTable from "../components/customerImport/CustomerImportValidationTable";
import CustomerImportPreviewTable from "../components/customerImport/CustomerImportPreviewTable";
import CustomerImportResultSummary from "../components/customerImport/CustomerImportResultSummary";
import { parseImportFile } from "../utils/importFileParser";
import { FALLBACK_IMPORT_PROFILES, getProfileByKey } from "../utils/clientImportProfiles";

const IMPORT_FIELDS = [
  "bedrijfsnaam",
  "klantnummer",
  "status",
  "bezoekadres",
  "factuuradres",
  "kvk",
  "btw",
  "hoofdcontactpersoon",
  "telefoon",
  "email",
  "klanttype",
];

const HEADER_LIKE_VALUES = new Set([
  "bedrijfsnaam",
  "naam",
  "company",
  "companyname",
  "klantnummer",
  "status",
  "bezoekadres",
  "factuuradres",
  "kvk",
  "btw",
  "hoofdcontactpersoon",
  "telefoon",
  "email",
  "e-mail",
  "klanttype",
]);

function isHeaderLikeMappedRow(row) {
  const values = Object.values(row || {})
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  if (values.length === 0) return false;
  const matches = values.filter((value) => HEADER_LIKE_VALUES.has(value)).length;
  return matches >= 2;
}

function autoMap(headers, profile) {
  const mapped = {};
  const aliases = profile?.defaultColumnAliases || {};
  const normalizedHeaders = headers.map((item) => String(item || "").trim().toLowerCase());

  IMPORT_FIELDS.forEach((field) => {
    const fieldAliases = Array.isArray(aliases[field]) ? aliases[field] : [];
    const idx = normalizedHeaders.findIndex((header) => fieldAliases.includes(header));
    mapped[field] = idx >= 0 ? headers[idx] : "";
  });

  return mapped;
}

function rowsFromMapping(rawRows, mapping) {
  return rawRows.map((row) => {
    const output = {};
    IMPORT_FIELDS.forEach((field) => {
      const header = mapping[field];
      output[field] = header ? String(row[header] || "").trim() : "";
    });
    return output;
  }).filter((row) => !isHeaderLikeMappedRow(row));
}

function ErrorPanel({ messages }) {
  const list = Array.isArray(messages) ? messages.filter(Boolean) : [];
  if (list.length === 0) return null;

  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
      <p className="mb-1 font-semibold">Importfouten</p>
      <ul className="list-disc pl-4">
        {list.slice(0, 8).map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export default function CustomersImportPage() {
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState(FALLBACK_IMPORT_PROFILES);
  const [selectedProfileKey, setSelectedProfileKey] = useState("generic_csv");
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState(() => ({}));
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [strategy, setStrategy] = useState("skip");
  const [result, setResult] = useState(null);
  const [pageErrors, setPageErrors] = useState([]);
  const [detectedDelimiter, setDetectedDelimiter] = useState("");
  const [detectedFileType, setDetectedFileType] = useState("");
  const [detectedSheetName, setDetectedSheetName] = useState("");

  const selectedProfile = useMemo(
    () => getProfileByKey(profiles, selectedProfileKey),
    [profiles, selectedProfileKey]
  );

  const mappedRows = useMemo(() => rowsFromMapping(rawRows, mapping), [rawRows, mapping]);

  useEffect(() => {
    let cancelled = false;
    async function loadProfiles() {
      try {
        const res = await api.get("/clients/import/profiles");
        const next = Array.isArray(res?.data?.profiles) && res.data.profiles.length > 0
          ? res.data.profiles
          : FALLBACK_IMPORT_PROFILES;
        if (!cancelled) {
          setProfiles(next);
          setSelectedProfileKey((prev) => (next.find((item) => item.key === prev) ? prev : (next[0]?.key || "generic_csv")));
        }
      } catch {
        if (!cancelled) setProfiles(FALLBACK_IMPORT_PROFILES);
      }
    }

    loadProfiles();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setPreview(null);
    setResult(null);
    setPageErrors([]);

    try {
      const parsed = await parseImportFile(file, {
        delimiterPreference: selectedProfile?.delimiterPreference,
      });

      setHeaders(parsed.headers);
      setRawRows(parsed.rows);
      setDetectedDelimiter(parsed.delimiter || "");
      setDetectedFileType(parsed.fileType || "");
      setDetectedSheetName(parsed.sheetName || "");
      setMapping(autoMap(parsed.headers, selectedProfile));
    } catch (error) {
      setHeaders([]);
      setRawRows([]);
      setMapping({});
      setDetectedDelimiter("");
      setDetectedFileType("");
      setDetectedSheetName("");
      setPageErrors([error?.message || "Bestand kon niet worden gelezen."]);
    }
  };

  const changeMapping = (field, value) => {
    setMapping((prev) => ({ ...prev, [field]: value }));
  };

  const requestPreview = async () => {
    if (!mappedRows.length) {
      setPageErrors(["Geen rijen gevonden om te valideren."]);
      return;
    }

    try {
      setPreviewLoading(true);
      setPageErrors([]);
      setResult(null);
      const res = await api.post("/clients/import/preview", {
        rows: mappedRows,
        sourceType: selectedProfileKey,
        filename: fileName,
      });
      setPreview(res.data || null);
    } catch (err) {
      setPageErrors([err?.response?.data?.error || "Preview genereren mislukt."]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const commitImport = async () => {
    if (!preview?.previewId) {
      setPageErrors(["Genereer eerst een preview."]);
      return;
    }

    try {
      setCommitLoading(true);
      setPageErrors([]);
      const res = await api.post("/clients/import/commit", {
        previewId: preview.previewId,
        strategy,
      });
      setResult(res.data || null);
    } catch (err) {
      setPageErrors([err?.response?.data?.error || "Import uitvoeren mislukt."]);
    } finally {
      setCommitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-slate-900 p-2 text-white">
                <Building2 size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Logboek24</p>
                <h1 className="text-2xl font-semibold text-slate-900">Klanten importeren</h1>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/klanten")}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
            >
              Terug naar klanten
            </button>
            <button
              type="button"
              onClick={() => navigate("/klanten/import/historie")}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
            >
              Importhistorie bekijken
            </button>
          </div>
        </header>
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">0. Bronprofiel kiezen</h3>
              <p className="mt-1 text-sm text-slate-500">Profiel verbetert auto-mapping en delimiter-detectie.</p>
              <div className="mt-3 grid gap-2 md:grid-cols-3">
                {profiles.map((profile) => {
                  const active = selectedProfileKey === profile.key;
                  return (
                    <button
                      key={profile.key}
                      type="button"
                      onClick={() => {
                        setSelectedProfileKey(profile.key);
                        if (headers.length > 0) {
                          setMapping(autoMap(headers, profile));
                        }
                      }}
                      className={`rounded-lg border p-3 text-left ${active ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"}`}
                    >
                      <p className="text-sm font-semibold text-slate-900">{profile.label}</p>
                      <p className="mt-1 text-xs text-slate-500">{profile.description}</p>
                    </button>
                  );
                })}
              </div>
        </section>

        <CustomerImportUploadCard fileName={fileName} onFileChange={handleFileChange} />

        {detectedDelimiter && (
          <p className="text-xs text-slate-500">Gedetecteerde delimiter: {detectedDelimiter === ";" ? "puntkomma" : "komma"}</p>
        )}
        {detectedFileType && (
          <p className="text-xs text-slate-500">Bestandstype: {detectedFileType.toUpperCase()}</p>
        )}
        {detectedSheetName && (
          <p className="text-xs text-slate-500">Werkblad: {detectedSheetName}</p>
        )}

        <CustomerImportMappingStep
          headers={headers}
          mapping={mapping}
          onChange={changeMapping}
          onPreview={requestPreview}
          loading={previewLoading}
          disabled={rawRows.length === 0}
        />

        <CustomerImportValidationTable summary={preview?.summary} />
        <CustomerImportPreviewTable rows={preview?.rows} />

        {preview?.rows?.length > 0 && !result && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-base font-semibold text-slate-900">5. Definitief importeren</h3>
                <p className="mt-1 text-sm text-slate-500">Duplicate-strategie geldt voor zowel exact als mogelijke duplicates.</p>

                <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-slate-700">Duplicate-strategie</span>
                    <select
                      value={strategy}
                      onChange={(event) => setStrategy(event.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="skip">Overslaan: veilige keuze, wijzigt geen bestaande duplicates</option>
                      <option value="create">Nieuw aanmaken: probeert duplicate alsnog als nieuw record</option>
                      <option value="update">Bijwerken: update bestaande duplicate-records</option>
                    </select>
                  </label>

                  <button
                    type="button"
                    onClick={commitImport}
                    disabled={commitLoading}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
                  >
                    {commitLoading ? "Importeren..." : "Import definitief uitvoeren"}
                  </button>
                </div>
          </div>
        )}

        <CustomerImportResultSummary
          result={result}
          onBackToList={() => navigate("/klanten")}
          onViewClients={() => navigate("/klanten")}
        />

        <ErrorPanel messages={pageErrors} />
      </div>
    </div>
  );
}
